import { Form, FormikErrors, useFormikContext } from 'formik';
import CampaignFormBase, {
    BaseCampaignFormValues,
    getBaseCampaignFormFields,
    mapCampaignApiErrorsToFormikErrors,
} from '../../components/CampaignFormBase/CampaignFormBase';
import { FormField, assets } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import styles from './TiktokCampaignForm.module.css';
import { useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import { buildTiktokMusicUrl, showNotification } from 'helpers';

const { Cross: CloseIcon, InfoCircleFilled: InfoIcon, Copy: CopyIcon } = assets;

export type TiktokCampaignFormValues = BaseCampaignFormValues & {
    audio_identifier: string;
};

type Props = {
    readonlyFields?: Array<keyof TiktokCampaignFormValues>;
    teamId: number | undefined;
    campaignId?: number;
    paymentRequestId?: number | null;
    isManagedExternally?: boolean;
    audioTitle?: string;
    audioAuthor?: string;
    audioIdentifier?: string;
};

const TiktokCampaignForm = ({
    readonlyFields,
    teamId,
    campaignId,
    paymentRequestId,
    isManagedExternally,
    audioTitle,
    audioAuthor,
    audioIdentifier,
}: Props) => {
    const { setFieldValue } = useFormikContext<TiktokCampaignFormValues>();
    const [isCreateAudioMode, setIsCreateAudioMode] = useState<boolean | undefined>(!audioIdentifier);

    useEffect(() => {
        setIsCreateAudioMode(!audioIdentifier);
    }, [audioIdentifier]);

    return (
        <Form className={styles.form}>
            <CampaignFormBase
                readonlyFields={getBaseCampaignFormFields(readonlyFields)}
                teamId={teamId}
                campaignId={campaignId}
                paymentRequestId={paymentRequestId}
                isManagedExternally={isManagedExternally}
            />

            {isCreateAudioMode ? (
                <FormField
                    name="audio_identifier"
                    label="Audio"
                    placeholder="Enter audio id or url"
                    disabled={readonlyFields?.includes('audio_identifier')}
                />
            ) : (
                <div className={styles.audioSection}>
                    <label className={styles.audioLabel}>Audio</label>
                    <div className={styles.audioBadge}>
                        <div className={styles.audioInfo}>
                            {!audioAuthor && !audioTitle ? (
                                buildTiktokMusicUrl(audioTitle ?? '', audioIdentifier ?? '')
                            ) : (
                                <span>
                                    {audioAuthor} - {audioTitle}
                                </span>
                            )}
                        </div>
                        <div className={styles.audioActions}>
                            <CopyIcon
                                className={styles.copyIcon}
                                onClick={() => {
                                    const success = copy(buildTiktokMusicUrl(audioTitle ?? '', audioIdentifier ?? ''));
                                    if (success) {
                                        showNotification('Copied!', 'info');
                                    }
                                }}
                            />
                            {!readonlyFields?.includes('audio_identifier') ? (
                                <CloseIcon
                                    className={styles.closeIcon}
                                    onClick={() => {
                                        setFieldValue('audio_identifier', '');
                                        setIsCreateAudioMode(true);
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                    {!audioAuthor && !audioTitle ? (
                        <div className={styles.audioStatusUnavailable}>
                            <InfoIcon className={styles.infoIcon} />
                            <span className={styles.audioStatus}>Audio details unavailable</span>
                        </div>
                    ) : null}
                </div>
            )}
        </Form>
    );
};

export const mapTiktokCampaignApiErrorsToFormikErrors = (
    apiErrors: creatorbase.PostCampaignErrorResponse | creatorbase.PatchCampaignErrorResponse
): FormikErrors<TiktokCampaignFormValues> => {
    return {
        ...mapCampaignApiErrorsToFormikErrors(apiErrors),
        ...mapApiErrorsToFormikErrors({
            audio_identifier: apiErrors.tiktok_details?.audio_identifier,
        }),
    };
};

export default TiktokCampaignForm;
